import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Globe, Server, Mail, Check, ArrowUpRight } from "lucide-react";
import { Reveal } from "../primitives/Reveal";
import { AccentTitle } from "../primitives/AccentTitle";

const EASE = [0.16, 1, 0.3, 1];

const TldMini = () => (
    <div className="flex flex-wrap gap-2">
        {[".sa", ".com.sa", ".org.sa", ".net.sa"].map((t) => (
            <span key={t} className="inline-flex h-9 items-center rounded-[6px] border-2 border-white/30 bg-white/10 px-3 font-mono text-[14px] font-semibold text-white num-ltr">{t}</span>
        ))}
    </div>
);

const DnsMini = () => (
    <div className="rounded-[8px] border-2 border-ink-200 bg-mudbrick-100 p-4 font-mono text-[14px] text-ink-800 num-ltr">
        {[
            { t: "A", v: "185.199.108.153" },
            { t: "MX", v: "10 mail.nour.net" },
            { t: "TXT", v: "v=spf1 ~all" },
        ].map((r, i) => (
            <div key={r.t} className="flex items-center gap-2 py-1">
                <span className="inline-block h-2 w-2 rounded-full bg-redsea-500" style={{ animation: `pulse-soft 1.6s ease-in-out ${i * 0.25}s infinite` }} />
                <span className="w-12 shrink-0 font-bold text-madder-500">{r.t}</span>
                <span className="truncate">{r.v}</span>
            </div>
        ))}
    </div>
);

const MailMini = () => (
    <div className="space-y-2">
        {[
            { l: "From: layla@thaqafa.sa", c: "text-white" },
            { l: "Subject: Welcome to NourNet", c: "text-white" },
            { l: "DKIM ✓ · SPF ✓ · DMARC ✓", c: "text-souq-200" },
        ].map((r, i) => (
            <div key={r.l} className={`rounded-[8px] border-2 border-white/30 bg-white/10 px-3 py-2.5 font-mono text-[13px] font-semibold ${r.c} num-ltr`}>
                {r.l}
            </div>
        ))}
    </div>
);

export const Services = () => {
    const { t } = useTranslation();

    return (
        <section id="services" className="scroll-mt-24 bg-paper-grain py-24 lg:py-32" data-testid="services-section">
            <div className="mx-auto max-w-[1320px] px-5 lg:px-8">
                <Reveal className="max-w-3xl">
                    <p className="eyebrow">{t("home.services.kicker")}</p>
                    <AccentTitle
                        component="h2"
                        className="section-headline mt-5"
                        title={t("home.services.title")}
                        accent={t("home.services.titleAccent")}
                        accentColor="#8A1E12"
                    />
                    <p className="body-prose mt-6 max-w-2xl">{t("home.services.subtitle")}</p>
                </Reveal>

                {/* Bento Tetris grid: tall email card + 2 wide cards */}
                <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-7" data-testid="services-bento">
                    {/* Domains — wide top-left, redsea teal solid */}
                    <motion.article
                        id="domains"
                        initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.55, ease: EASE }}
                        className="bg-redsea-grain group lg:col-span-2 relative overflow-hidden rounded-card p-8 lg:p-10 text-white shadow-redsea transition-transform duration-300 ease-nour hover:-translate-y-1 scroll-mt-24"
                        data-testid="service-card-domains"
                    >
                        <div className="flex items-start justify-between gap-6 flex-wrap">
                            <div className="max-w-[58%]">
                                <div className="inline-flex h-14 w-14 items-center justify-center rounded-[10px] bg-white text-redsea-500">
                                    <Globe className="h-7 w-7" strokeWidth={2.4} />
                                </div>
                                <h3 className="mt-6 font-display text-[34px] font-bold leading-tight tracking-tight">
                                    {t("home.services.domains.title")}
                                </h3>
                                <p className="mt-4 text-[18px] leading-relaxed text-redsea-100">
                                    {t("home.services.domains.desc")}
                                </p>
                            </div>
                            <div className="w-full max-w-[44%] mt-2">
                                <TldMini />
                            </div>
                        </div>
                        <ul className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[16px]">
                            {[1, 2, 3].map((n) => (
                                <li key={n} className="flex items-start gap-2 font-semibold">
                                    <Check className="mt-1 h-4 w-4 shrink-0 text-souq-300" strokeWidth={3} />
                                    <span>{t(`home.services.domains.bullet${n}`)}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-7 inline-flex items-center gap-1.5 text-[16px] font-bold">
                            <span className="border-b-2 border-souq-400 pb-0.5">{t("cta.viewAll")}</span>
                            <ArrowUpRight className="h-5 w-5 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 rtl-mirror" />
                        </div>
                    </motion.article>

                    {/* Email — tall right column, madder solid */}
                    <motion.article
                        id="email"
                        initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.55, ease: EASE, delay: 0.08 }}
                        className="bg-madder-grain group lg:row-span-2 relative overflow-hidden rounded-card p-8 lg:p-10 text-white shadow-madder transition-transform duration-300 ease-nour hover:-translate-y-1 scroll-mt-24"
                        data-testid="service-card-email"
                    >
                        <div className="inline-flex h-14 w-14 items-center justify-center rounded-[10px] bg-white text-madder-500">
                            <Mail className="h-7 w-7" strokeWidth={2.4} />
                        </div>
                        <h3 className="mt-6 font-display text-[34px] font-bold leading-tight tracking-tight">
                            {t("home.services.email.title")}
                        </h3>
                        <p className="mt-4 text-[18px] leading-relaxed text-madder-100">
                            {t("home.services.email.desc")}
                        </p>
                        <div className="mt-7"><MailMini /></div>
                        <ul className="mt-7 space-y-3 text-[16px]">
                            {[1, 2, 3].map((n) => (
                                <li key={n} className="flex items-start gap-2 font-semibold">
                                    <Check className="mt-1 h-4 w-4 shrink-0 text-souq-300" strokeWidth={3} />
                                    <span>{t(`home.services.email.bullet${n}`)}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-9 inline-flex items-center gap-1.5 text-[16px] font-bold">
                            <span className="border-b-2 border-souq-400 pb-0.5">{t("cta.viewAll")}</span>
                            <ArrowUpRight className="h-5 w-5 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 rtl-mirror" />
                        </div>
                    </motion.article>

                    {/* DNS — wide bottom-left, white card with souq accent */}
                    <motion.article
                        initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.55, ease: EASE, delay: 0.16 }}
                        className="bento-card group lg:col-span-2 relative overflow-hidden p-8 lg:p-10"
                        data-testid="service-card-dns"
                    >
                        <div className="flex items-start justify-between gap-6 flex-wrap">
                            <div className="max-w-[58%]">
                                <div className="inline-flex h-14 w-14 items-center justify-center rounded-[10px] bg-souq-400 text-ink-900">
                                    <Server className="h-7 w-7" strokeWidth={2.4} />
                                </div>
                                <h3 className="mt-6 font-display text-[34px] font-bold leading-tight tracking-tight text-ink-800">
                                    {t("home.services.dns.title")}
                                </h3>
                                <p className="body-prose mt-4">
                                    {t("home.services.dns.desc")}
                                </p>
                            </div>
                            <div className="w-full max-w-[44%]">
                                <DnsMini />
                            </div>
                        </div>
                        <ul className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[16px]">
                            {[1, 2, 3].map((n) => (
                                <li key={n} className="flex items-start gap-2 font-semibold text-ink-800">
                                    <Check className="mt-1 h-4 w-4 shrink-0 text-madder-500" strokeWidth={3} />
                                    <span>{t(`home.services.dns.bullet${n}`)}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-7 inline-flex items-center gap-1.5 text-[16px] font-bold text-madder-500">
                            <span className="border-b-2 border-madder-500 pb-0.5">{t("cta.viewAll")}</span>
                            <ArrowUpRight className="h-5 w-5 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 rtl-mirror" />
                        </div>
                    </motion.article>
                </div>
            </div>
        </section>
    );
};
